import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { TrainingSprintStepProps } from '../types'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../../constants/colors'
import { ScheduleActivityItem, ScheduledActivity } from '../components/ScheduleActivityItem/ScheduleActivityItem'
import { NumberTracker } from '../../../../interaxDesignSystemComponents'
import {
  findOutOfOrderActivities,
  isActivityInPast,
  getOverlappingActivityIndices,
  getActivitiesWithoutBreak,
  isGameActivity
} from '../config'

export const ScheduleSessionsStep: React.FC<TrainingSprintStepProps> = ({
  data,
  updateData,
  stepId: _stepId,
  isActive: _isActive,
  isCompleted: _isCompleted,
  isVisited: _isVisited,
  hasValidationErrors = false
}) => {
  const { t } = useTranslation()
  const [scheduledActivities, setScheduledActivities] = useState<ScheduledActivity[]>([])

  // Initialize scheduled activities from the selected activities in step 2
  useEffect(() => {
    if (data.activities && data.activities.length > 0) {
      const initialActivities = data.activities.map((activity) => ({
        ...activity,
        scheduledDate: activity.scheduledDate || undefined,
        scheduledTime: activity.scheduledTime || undefined,
        endTime: activity.endTime || undefined
      }))
      setScheduledActivities(initialActivities)
    }
  }, [data.activities])

  // Check which activities are out of chronological order
  const outOfOrderIndices = useMemo(() => {
    return findOutOfOrderActivities(scheduledActivities)
  }, [scheduledActivities])

  // Check which activities overlap with their predecessor
  const overlappingIndices = useMemo(() => {
    return getOverlappingActivityIndices(scheduledActivities)
  }, [scheduledActivities])

  // Check which activities don't have the required 5-minute break after a game
  const withoutBreakIndices = useMemo(() => {
    return getActivitiesWithoutBreak(scheduledActivities)
  }, [scheduledActivities])

  // Handle activity scheduling
  const handleScheduleActivity = (activityId: string, date: string, startTime: string, endTime: string) => {
    const updatedActivities = scheduledActivities.map((activity) => {
      if (activity.id === activityId) {
        return {
          ...activity,
          scheduledDate: date,
          scheduledTime: startTime,
          endTime: endTime
        }
      }
      return activity
    })

    setScheduledActivities(updatedActivities)

    // Update the main data object with scheduled activities
    updateData('activities', updatedActivities)
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2
          className="font-bold mb-4"
          style={{
            color: INTERAX_COLORS.typographyText,
            fontFamily: INTERAX_TYPOGRAPHY.headline2.fontFamily,
            fontWeight: INTERAX_TYPOGRAPHY.headline2.fontWeight,
            fontSize: INTERAX_TYPOGRAPHY.headline2.fontSize,
            lineHeight: INTERAX_TYPOGRAPHY.headline2.lineHeight
          }}
        >
          {t('facilitator.trainingSprint.scheduleSessions.title')}
        </h2>
        <p
          className="text-lg"
          style={{
            color: INTERAX_COLORS.typographyFont,
            fontFamily: INTERAX_TYPOGRAPHY.body1.fontFamily,
            fontWeight: INTERAX_TYPOGRAPHY.body1.fontWeight,
            fontSize: INTERAX_TYPOGRAPHY.body1.fontSize,
            lineHeight: INTERAX_TYPOGRAPHY.body1.lineHeight
          }}
        >
          {t('facilitator.trainingSprint.scheduleSessions.subtitle')}
        </p>
      </div>

      {/* Activity Schedule */}
      <div
        className="p-6 rounded-lg border"
        style={{
          backgroundColor: INTERAX_COLORS.heritageBlue40,
          borderColor: INTERAX_COLORS.heritageBlue
        }}
      >
        <h3
          className="font-semibold mb-6"
          style={{
            color: INTERAX_COLORS.white,
            fontFamily: INTERAX_TYPOGRAPHY.headline3.fontFamily,
            fontWeight: INTERAX_TYPOGRAPHY.headline3.fontWeight,
            fontSize: INTERAX_TYPOGRAPHY.headline3.fontSize,
            lineHeight: INTERAX_TYPOGRAPHY.headline3.lineHeight
          }}
        >
          Activity Schedule ({scheduledActivities.length} activities)
        </h3>

        {scheduledActivities.length === 0 ? (
          <div
            className="text-center py-12"
            style={{
              color: INTERAX_COLORS.thunderGrey,
              fontFamily: INTERAX_TYPOGRAPHY.body2.fontFamily,
              fontWeight: INTERAX_TYPOGRAPHY.body2.fontWeight,
              fontSize: INTERAX_TYPOGRAPHY.body2.fontSize,
              lineHeight: INTERAX_TYPOGRAPHY.body2.lineHeight
            }}
          >
            {t('facilitator.trainingSprint.scheduleSessions.noActivitiesSelected')}
          </div>
        ) : (
          <div className="flex gap-6">
            {/* Number Tracker */}
            <NumberTracker items={scheduledActivities} itemHeight={80} itemSpacing={16} visible={true} />

            {/* Activities List */}
            <div className="flex-1 space-y-4">
              {scheduledActivities.map((activity, index) => {
                // Check if this specific activity is unscheduled when validation errors exist
                const isUnscheduled =
                  hasValidationErrors && (!activity.scheduledDate || !activity.scheduledTime || !activity.endTime)

                // Check if this activity is out of chronological order
                const isOutOfOrder = outOfOrderIndices.includes(index)

                // Check if this activity is scheduled in the past
                const isPastActivity = hasValidationErrors && isActivityInPast(activity)

                // Check if this activity overlaps with previous activity
                const isOverlapping = overlappingIndices.includes(index)

                // Check if this activity doesn't have the required break after a game
                const isWithoutBreak = withoutBreakIndices.includes(index)

                // Show error if any validation error exists
                const hasError = isUnscheduled || isOutOfOrder || isPastActivity || isOverlapping || isWithoutBreak

                // Determine error message priority: break > overlap > past > out of order
                let errorMessage: string | undefined
                if (isWithoutBreak) {
                  // Use different error message if the current activity is also a game session
                  const isCurrentGame = isGameActivity(activity.type)
                  const prevActivity = index > 0 ? scheduledActivities[index - 1] : null
                  const isPrevGame = prevActivity ? isGameActivity(prevActivity.type) : false

                  if (isCurrentGame && isPrevGame) {
                    errorMessage = t('facilitator.trainingSprint.validation.gameActivityBreakRequiredGameToGame')
                  } else {
                    errorMessage = t('facilitator.trainingSprint.validation.gameActivityBreakRequired')
                  }
                } else if (isOverlapping) {
                  errorMessage = t('facilitator.trainingSprint.validation.activitiesOverlap')
                } else if (isPastActivity) {
                  errorMessage = t('facilitator.trainingSprint.validation.activityStartsInPast')
                } else if (isOutOfOrder) {
                  errorMessage = t('facilitator.trainingSprint.validation.activityMustBeAfterPrevious')
                }

                return (
                  <ScheduleActivityItem
                    key={activity.id}
                    activity={activity}
                    index={index + 1}
                    previousActivity={index > 0 ? scheduledActivities[index - 1] : undefined}
                    onSchedule={handleScheduleActivity}
                    hasError={hasError}
                    errorMessage={errorMessage}
                  />
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
